import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../store/themeStore';

/**
 * Ingresos y ganancia a lo largo del tiempo.
 *
 * ## Los colores no se eligieron a ojo
 *
 * Salen de los propios tonos del proyecto y están pasados por el validador de
 * paletas: separación para daltonismo, suelo de croma, banda de luminosidad y
 * contraste contra el fondo.
 *
 * Y son **distintos en claro y en oscuro**, que es lo que casi nadie hace. Los
 * tonos 500 sobre fondo oscuro se salen de la banda de luminosidad —quedan
 * demasiado claros y deslumbran—, así que en oscuro se usan los 600. No es
 * invertir la paleta: es elegir otro paso de la misma rampa.
 *
 *   claro    ingresos #0ea5e9   ganancia #22c55e
 *   oscuro   ingresos #0284c7   ganancia #16a34a
 *
 * El contraste en modo claro se queda por debajo de 3:1 contra el blanco de la
 * tarjeta, y eso **obliga** a que la identidad de cada serie no dependa solo del
 * color: de ahí la leyenda con su nombre escrito y los valores en el globo.
 *
 * ## Una sola escala
 *
 * Ingresos y ganancia comparten eje a propósito. Dos ejes distintos —uno a cada
 * lado— es el error más común de este tipo de gráfica: deja colocar las dos
 * líneas donde uno quiera y sugiere cruces que no existen. Aquí la distancia
 * entre las dos líneas **es** el coste, y se lee sin explicar nada.
 *
 * ## Se dibuja al tamaño real, medido
 *
 * La primera versión usaba un `viewBox` fijo de 720 y dejaba que el navegador
 * lo escalara. Las líneas se salvaban con `vectorEffect`, pero **el texto no**:
 * a 375 píxeles de ancho el factor es 0,48, así que las etiquetas de los ejes
 * quedaban en unos seis píxeles. Se vio en la captura del móvil y no había
 * forma de leerlas. Midiendo el hueco, doce píxeles son doce en toda pantalla.
 */

const COLORES = {
    claro: { ingresos: '#0ea5e9', ganancia: '#22c55e', reja: '#e2e8f0', texto: '#64748b', fondo: '#ffffff' },
    oscuro: { ingresos: '#0284c7', ganancia: '#16a34a', reja: '#334155', texto: '#94a3b8', fondo: '#1e293b' }
};

const ALTO = 260;
const MARGEN = { arriba: 16, derecha: 16, abajo: 28, izquierda: 56 };
const ANCHO_MINIMO = 240;

const dinero = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function GraficaEvolucion({ datos = [] }) {
    const { t, i18n } = useTranslation();
    const { isDark } = useThemeStore();
    const [activo, setActivo] = useState(null);
    const [ancho, setAncho] = useState(0);
    const cajaRef = useRef(null);

    // El contenedor medido es SIEMPRE el mismo elemento, pase lo que pase con
    // el contenido. Si cambiara al pasar de «sin medir» a «dibujado», el
    // observador se quedaría mirando un elemento ya desmontado y la gráfica no
    // volvería a reaccionar a un cambio de tamaño de la ventana.
    useEffect(() => {
        const caja = cajaRef.current;
        if (!caja) return;

        const medir = () => setAncho(caja.clientWidth);
        medir();

        const observador = new ResizeObserver(medir);
        observador.observe(caja);
        return () => observador.disconnect();
    }, []);

    const c = isDark ? COLORES.oscuro : COLORES.claro;
    const listo = datos.length >= 2 && ancho >= ANCHO_MINIMO;

    const anchoUtil = ancho - MARGEN.izquierda - MARGEN.derecha;
    const altoUtil = ALTO - MARGEN.arriba - MARGEN.abajo;

    // El techo sale de los ingresos, que siempre son mayores o iguales que la
    // ganancia. Un mínimo de 10 evita que una gráfica de ceros se dibuje como
    // una línea pegada al borde de arriba.
    const techo = Math.max(10, ...datos.map((d) => Number(d.revenue) || 0));

    const x = (i) => MARGEN.izquierda + (i * anchoUtil) / (datos.length - 1);
    const y = (v) => MARGEN.arriba + altoUtil - ((Number(v) || 0) / techo) * altoUtil;

    const linea = (campo) => datos.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${y(d[campo])}`).join(' ');

    const fecha = (iso) =>
        new Date(`${iso}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

    /** Qué punto queda bajo el ratón. */
    const alMover = (e) => {
        const caja = cajaRef.current?.getBoundingClientRect();
        if (!caja) return;
        const i = Math.round(((e.clientX - caja.left - MARGEN.izquierda) / anchoUtil) * (datos.length - 1));
        setActivo(i >= 0 && i < datos.length ? i : null);
    };

    const punto = listo && activo !== null ? datos[activo] : null;
    const marcas = listo ? [0, 0.5, 1].map((p) => ({ valor: techo * p, y: y(techo * p) })) : [];

    return (
        <div className="relative" ref={cajaRef}>
            {/* La leyenda va siempre con dos series. No es decoración: el
                contraste de estos tonos contra la tarjeta blanca obliga a que
                el nombre esté escrito, no solo pintado. */}
            <div className="flex items-center gap-4 mb-3 text-sm">
                {[['ingresos', c.ingresos, 'analytics.revenue'], ['ganancia', c.ganancia, 'analytics.net_profit']].map(
                    ([clave, color, etiqueta]) => (
                        <span key={clave} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                            {t(etiqueta)}
                        </span>
                    )
                )}
            </div>

            {listo && (
                <svg
                    width={ancho}
                    height={ALTO}
                    viewBox={`0 0 ${ancho} ${ALTO}`}
                    className="block"
                    role="img"
                    aria-label={t('analytics.chart_alt')}
                    onMouseMove={alMover}
                    onMouseLeave={() => setActivo(null)}
                >
                    {/* La reja va detrás y en un tono apagado: es referencia, no dato. */}
                    {marcas.map((m) => (
                        <g key={m.valor}>
                            <line
                                x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha}
                                y1={m.y} y2={m.y}
                                stroke={c.reja} strokeWidth="1"
                            />
                            <text x={MARGEN.izquierda - 8} y={m.y + 4} textAnchor="end" fontSize="12" fill={c.texto}>
                                {dinero(m.valor)}
                            </text>
                        </g>
                    ))}

                    {/* Solo tres fechas: una etiqueta por día se solapa y no se lee. */}
                    {[0, Math.floor(datos.length / 2), datos.length - 1].map((i) => (
                        <text
                            key={i}
                            x={x(i)} y={ALTO - 8}
                            textAnchor={i === 0 ? 'start' : i === datos.length - 1 ? 'end' : 'middle'}
                            fontSize="12" fill={c.texto}
                        >
                            {fecha(datos[i].date)}
                        </text>
                    ))}

                    <path d={linea('revenue')} fill="none" stroke={c.ingresos} strokeWidth="2" />
                    <path d={linea('profit')} fill="none" stroke={c.ganancia} strokeWidth="2" />

                    {punto && (
                        <g>
                            <line
                                x1={x(activo)} x2={x(activo)} y1={MARGEN.arriba} y2={ALTO - MARGEN.abajo}
                                stroke={c.texto} strokeWidth="1" strokeDasharray="4 4"
                            />
                            {/* El anillo del color del fondo separa el punto de
                                la línea que tiene debajo. */}
                            <circle cx={x(activo)} cy={y(punto.revenue)} r="5" fill={c.ingresos} stroke={c.fondo} strokeWidth="2" />
                            <circle cx={x(activo)} cy={y(punto.profit)} r="5" fill={c.ganancia} stroke={c.fondo} strokeWidth="2" />
                        </g>
                    )}
                </svg>
            )}

            {punto && (
                <div
                    className="absolute top-8 pointer-events-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap"
                    style={{
                        left: `${x(activo)}px`,
                        transform: activo > datos.length / 2 ? 'translateX(-105%)' : 'translateX(5%)'
                    }}
                >
                    <p className="font-medium text-slate-900 dark:text-white mb-1">{fecha(punto.date)}</p>
                    <p style={{ color: c.ingresos }}>{t('analytics.revenue')}: {dinero(punto.revenue)}</p>
                    <p style={{ color: c.ganancia }}>{t('analytics.net_profit')}: {dinero(punto.profit)}</p>
                    <p className="text-slate-500 mt-1">{t('analytics.orders_n', { count: punto.orders })}</p>
                </div>
            )}
        </div>
    );
}
